/**
 * src/components/agent/ScopeChipRow.jsx — Horizontal scope chip row.
 *
 * Phase 82-04 — Chat System Unification, header.
 *
 * Props:
 *   scope       Array<ScopeEntry> — current chat scope
 *   onAdd       (entry) => void   — invoked after picker commits
 *   onRemove    (type, id) => void
 *
 * Icons keyed by type; colors come from chipPalette(). Port is NOT rendered —
 * scopeContract rejects it, but we also no-op here for defense in depth.
 */

import { useState, useRef, useCallback } from 'react';
import { Plus, X, Briefcase, DollarSign, CheckSquare, User } from 'lucide-react';
import ScopeChipPicker from './ScopeChipPicker';
import { useT } from '../../lib/i18n/I18nContext';
import {
  chipRowStyle,
  chipStyle,
  chipCloseBtnStyle,
  addChipBtnStyle,
} from './ChatHeader.styles';

// TICKET-1144 — chips say what they are, so a mixed scope reads at a glance.
const TYPE_LABEL = { project: 'Project', deal: 'Lead', task: 'Task', person: 'Person' };

/**
 * One line saying what the chat is narrowed to and where new tasks land —
 * "Agustina on MY Simulation · New tasks go to Agustina on MY Simulation".
 */
export function scopeSummary(entries, t) {
  const names = (type) => entries.filter(e => e.type === type).map(e => e.name || e.type);
  const people = names('person'), projects = names('project'), deals = names('deal'), tasks = names('task');
  const where = [...projects, ...deals, ...tasks].join(' + ');
  const headline = people.length ? `${people.join(' + ')}${where ? ` ${t('agentChat.scopeOn', { defaultValue: 'on' })} ${where}` : ''}` : where;
  const assignee = people[0] || t('agentChat.scopeYou', { defaultValue: 'you' });
  const target = projects[0] ? ` ${t('agentChat.scopeOn', { defaultValue: 'on' })} ${projects[0]}` : '';
  return { headline, landing: `${t('agentChat.scopeNewTasksGoTo', { defaultValue: 'New tasks go to' })} ${assignee}${target}` };
}

const TYPE_ICON = {
  project: Briefcase,
  deal: DollarSign,
  task: CheckSquare,
  person: User,
};

function ScopeChip({ entry, onRemove }) {
  const Icon = TYPE_ICON[entry.type];
  const label = entry.name || entry.type;
  return (
    <span
      style={chipStyle(entry.type)}
      data-testid={`scope-chip-${entry.type}-${entry.id}`}
      data-scope-type={entry.type}
    >
      {Icon ? <Icon size={12} strokeWidth={2} /> : null}
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{TYPE_LABEL[entry.type]}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160, color: 'var(--t1)' }}>{label}</span>
      <button
        type="button"
        onClick={() => onRemove(entry.type, entry.id)}
        aria-label={`Remove ${label}`}
        data-testid={`scope-chip-remove-${entry.type}-${entry.id}`}
        style={chipCloseBtnStyle}
      >
        <X size={12} />
      </button>
    </span>
  );
}

export default function ScopeChipRow({ scope, onAdd, onRemove }) {
  const { t } = useT();
  const [pickerOpen, setPickerOpen] = useState(false);
  const addBtnRef = useRef(null);

  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);

  const handleAdd = useCallback((entry) => {
    if (!entry || entry.type === 'port') return;
    onAdd?.(entry);
    setPickerOpen(false);
  }, [onAdd]);

  const entries = Array.isArray(scope) ? scope.filter(e => e && e.type !== 'port') : [];
  // TICKET-107 (a) — the chat's selected project constrains the picker's
  // task tab to in-scope tasks (picker offers a show-all escape hatch).
  const scopeProjectId = entries.find(e => e.type === 'project')?.id || null;
  const summary = entries.length ? scopeSummary(entries, t) : null;
  const clearAll = () => entries.forEach(e => onRemove?.(e.type, e.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ ...chipRowStyle, position: 'relative' }} data-testid="scope-chip-row">
      {entries.map((e) => (
        <ScopeChip key={`${e.type}:${e.id}`} entry={e} onRemove={onRemove} />
      ))}
      <button
        ref={addBtnRef}
        type="button"
        onClick={openPicker}
        style={addChipBtnStyle}
        data-testid="scope-chip-add"
        aria-label="Add scope"
        aria-expanded={pickerOpen}
      >
        <Plus size={14} />
        <span>{entries.length ? t('agentChat.scopeNarrow', { defaultValue: 'Narrow' }) : t('agentChat.addScope', { defaultValue: 'Add scope' })}</span>
      </button>
      {pickerOpen && (
        <ScopeChipPicker
          onAdd={handleAdd}
          onClose={closePicker}
          existing={entries}
          scopeProjectId={scopeProjectId}
        />
      )}
    </div>
    {summary && (
      <div
        data-testid="scope-summary"
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--t2)' }}
      >
        <span>
          <strong style={{ color: 'var(--t1)', fontWeight: 600 }}>{t('agentChat.scopeNarrowedTo', { defaultValue: 'Narrowed to' })}: {summary.headline}</strong>
          {' · '}{summary.landing}
        </span>
        <button
          type="button"
          onClick={clearAll}
          data-testid="scope-summary-clear"
          style={{ minHeight: 44, padding: '8px 12px', border: 'none', background: 'transparent', color: 'var(--accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          {t('agentChat.scopeClear', { defaultValue: 'Clear' })}
        </button>
      </div>
    )}
    </div>
  );
}
